// src/pages/Dashboard/Admin/Votes/hooks/useVotesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllVotes,
  createVote,
  updateVote,
  deleteVote,
} from "../../../../../services/voteServices";

import {
  DEFAULT_VOTE_FILTERS,
  buildVotePayload,
  calculateVotesStats,
  filterVotes,
  safeArray,
  sortVotes,
} from "../utils/votesDashboardUtils";

export function useVotesDashboard() {
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_VOTE_FILTERS);

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableVote, setEditableVote] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);

  const [selectedVote, setSelectedVote] = useState(null);
  const [questionsDrawerOpen, setQuestionsDrawerOpen] = useState(false);
  const [resultsDrawerOpen, setResultsDrawerOpen] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const fetchVotes = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllVotes();
      setVotes(sortVotes(safeArray(data)));
    } catch (error) {
      console.error("Erreur chargement votes :", error);
      toast.error("Erreur lors du chargement des votes.");
      setVotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVotes();
  }, [fetchVotes]);

  const filteredVotes = useMemo(() => {
    return filterVotes(votes, filters);
  }, [votes, filters]);

  const stats = useMemo(() => calculateVotesStats(votes), [votes]);

  const filteredStats = useMemo(
    () => calculateVotesStats(filteredVotes),
    [filteredVotes]
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_VOTE_FILTERS);
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableVote(null);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (vote) => {
    setFormMode("edit");
    setEditableVote(vote || null);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditableVote(null);
    setFormMode("create");
  };

  const handleCreateVote = async (formData) => {
    try {
      setSubmittingForm(true);

      await createVote(buildVotePayload(formData));

      toast.success("Vote / sondage créé avec succès.");
      await fetchVotes();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur création vote :", error);
      toast.error("Erreur lors de la création du vote.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleUpdateVote = async (formData) => {
    if (!editableVote?.id) {
      toast.error("Vote introuvable.");
      return;
    }

    try {
      setSubmittingForm(true);

      await updateVote(editableVote.id, buildVotePayload(formData));

      toast.success("Vote / sondage modifié avec succès.");
      await fetchVotes();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur modification vote :", error);
      toast.error("Erreur lors de la modification du vote.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleDeleteVote = async (vote) => {
    if (!vote?.id) return;

    if (
      !window.confirm(
        `Supprimer définitivement le vote « ${vote.title || "sans titre"} » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(vote.id);

      await deleteVote(vote.id);

      toast.success("Vote supprimé avec succès.");
      setVotes((prev) => prev.filter((item) => item.id !== vote.id));
    } catch (error) {
      console.error("Erreur suppression vote :", error);
      toast.error("Erreur lors de la suppression du vote.");
    } finally {
      setDeletingId(null);
    }
  };

  const openQuestionsDrawer = (vote) => {
    setSelectedVote(vote || null);
    setQuestionsDrawerOpen(true);
  };

  const closeQuestionsDrawer = () => {
    setQuestionsDrawerOpen(false);
    setSelectedVote(null);
  };

  const openResultsDrawer = (vote) => {
    setSelectedVote(vote || null);
    setResultsDrawerOpen(true);
  };

  const closeResultsDrawer = () => {
    setResultsDrawerOpen(false);
    setSelectedVote(null);
  };

  return {
    votes,
    filteredVotes,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,

    formDrawerOpen,
    formMode,
    editableVote,
    submittingForm,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleCreateVote,
    handleUpdateVote,

    selectedVote,
    questionsDrawerOpen,
    openQuestionsDrawer,
    closeQuestionsDrawer,

    resultsDrawerOpen,
    openResultsDrawer,
    closeResultsDrawer,

    deletingId,
    handleDeleteVote,

    fetchVotes,
  };
}